/**
 * Pure utility functions for minimap color resolution.
 * No Angular dependencies — importable from controllers and component alike.
 */

/** Opacity applied to row background colors in the minimap. */
export const MINIMAP_BG_ALPHA = 0.08;

/**
 * Returns true when the color is surface-like:
 * luminance > 0.93 (near-white), luminance < 0.04 (near-black),
 * or alpha < 0.15 (nearly transparent).
 */
export function isSurfaceLike(color: string): boolean {
  const m = /rgba?\(\s*([\d.]+)\s*(?:,\s*|\s+)([\d.]+)\s*(?:,\s*|\s+)([\d.]+)(?:\s*(?:,\s*|\/\s*)([\d.]+%?))?\s*\)/.exec(color);
  if (!m) return false;
  if (m[4]) {
    let a = parseFloat(m[4]);
    if (m[4].endsWith('%')) a /= 100;
    if (a < 0.15) return true;
  }
  const lum = 0.2126 * parseFloat(m[1]) / 255 + 0.7152 * parseFloat(m[2]) / 255 + 0.0722 * parseFloat(m[3]) / 255;
  return lum > 0.93 || lum < 0.04;
}

/**
 * Applies a fixed alpha value to an rgb/rgba color string.
 *
 * @param color  CSS color string in rgb() or rgba() notation.
 * @param alpha  Target alpha (default: MINIMAP_BG_ALPHA).
 */
export function toMinimapColor(color: string, alpha: number = MINIMAP_BG_ALPHA): string {
  const m = /rgba?\(\s*([\d.]+)\s*(?:,\s*|\s+)([\d.]+)\s*(?:,\s*|\s+)([\d.]+)/.exec(color);
  if (!m) return color;
  return `rgba(${Math.round(parseFloat(m[1]))},${Math.round(parseFloat(m[2]))},${Math.round(parseFloat(m[3]))},${alpha})`;
}

/** Parses a hex (#rgb, #rrggbb) or rgb/rgba() string to { r, g, b }. Returns null on failure. */
export function parseRgbValue(s: string): { r: number; g: number; b: number } | null {
  const hex6 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(s);
  if (hex6) return { r: parseInt(hex6[1], 16), g: parseInt(hex6[2], 16), b: parseInt(hex6[3], 16) };
  const hex3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(s);
  if (hex3) return { r: parseInt(hex3[1] + hex3[1], 16), g: parseInt(hex3[2] + hex3[2], 16), b: parseInt(hex3[3] + hex3[3], 16) };
  const rgb = /rgba?\(\s*([\d.]+)\s*(?:,\s*|\s+)([\d.]+)\s*(?:,\s*|\s+)([\d.]+)/.exec(s);
  if (rgb) return { r: Math.round(parseFloat(rgb[1])), g: Math.round(parseFloat(rgb[2])), b: Math.round(parseFloat(rgb[3])) };
  return null;
}

/**
 * Reads the base color from the CSS custom property --c-grad1.
 * Pattern: color-mix(in srgb, var(--color), transparent N%).
 * Returns empty string when the property is absent or unparseable.
 */
export function resolveColorFromVarChain(style: CSSStyleDeclaration): string {
  const grad1 = style.getPropertyValue('--c-grad1').trim();
  if (!grad1) return '';
  const m = /color-mix\(\s*in\s+srgb\s*,\s*(.+?)\s*,\s*transparent/i.exec(grad1);
  if (!m) return '';
  let baseColor = m[1].trim();
  const varRef = /var\(\s*(--[\w-]+)\s*\)/.exec(baseColor);
  if (varRef) baseColor = style.getPropertyValue(varRef[1]).trim();
  if (!baseColor) return '';
  const rgb = parseRgbValue(baseColor);
  return rgb ? `rgba(${rgb.r},${rgb.g},${rgb.b},${MINIMAP_BG_ALPHA})` : '';
}

/**
 * Extracts a color from a CSS background-image gradient string.
 * Handles rgba(r,g,b,a) and CSS Level 4 rgb(r g b / a%).
 * Un-premultiplies when alpha is very low (color-mix with transparent).
 * Returns empty string when the image is 'none' or contains no parseable color.
 */
export function resolveGradientColor(img: string): string {
  if (!img || img === 'none') return '';
  const match = /rgba?\(\s*([\d.]+)\s*(?:,\s*|\s+)([\d.]+)\s*(?:,\s*|\s+)([\d.]+)(?:\s*(?:,\s*|\/\s*)([\d.]+%?))?\s*\)/.exec(img);
  if (!match) return '';
  let r = parseFloat(match[1]); let g = parseFloat(match[2]); let b = parseFloat(match[3]);
  const alphaRaw = match[4] ?? '1';
  let a = parseFloat(alphaRaw);
  if (alphaRaw.endsWith('%')) a /= 100;
  if (a > 0 && a < 0.5) {
    r = Math.min(255, Math.round(r / a));
    g = Math.min(255, Math.round(g / a));
    b = Math.min(255, Math.round(b / a));
  }
  return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${MINIMAP_BG_ALPHA})`;
}
